// ═══════════════════════════════════════════════════════════════════════════════
// MSAL Configuration — Microsoft Entra ID (Azure AD) authentication
// Uses VITE_AZURE_CLIENT_ID and VITE_AZURE_TENANT_ID env vars.
// If either is missing, IS_MSAL_CONFIGURED is false and auth is bypassed
// with a warning banner in AuthGuard.
// ═══════════════════════════════════════════════════════════════════════════════
import { PublicClientApplication } from '@azure/msal-browser';
import type { AccountInfo, Configuration } from '@azure/msal-browser';
import {
  acquireBearer,
  apiScopeFor,
  clearInteractiveAuthFlag,
  ensureMsalReady,
  shouldStartInteractiveAuth,
} from './bearerBridge';
import type { BearerBridgeDeps, BearerReason, BearerResult, SwaIdentity } from './bearerBridge';

const clientId = import.meta.env.VITE_AZURE_CLIENT_ID as string | undefined;
const tenantId = import.meta.env.VITE_AZURE_TENANT_ID as string | undefined;

export const IS_MSAL_CONFIGURED = !!(clientId && tenantId);

export const loginRequest = {
  scopes: ['openid', 'profile', 'email', 'User.Read'],
};

// Only create the instance when fully configured — avoids MSAL errors in dev
// when env vars are not set.
export const msalInstance: PublicClientApplication | null = IS_MSAL_CONFIGURED
  ? new PublicClientApplication({
      auth: {
        clientId: clientId!,
        authority: `https://login.microsoftonline.com/${tenantId!}`,
        redirectUri: window.location.origin,
        postLogoutRedirectUri: window.location.origin,
        // Prevent extra navigation after redirect callback — MSAL defaults to
        // navigating back to the URL that started the login, which can remount
        // the app and lose in-memory auth state, causing a redirect loop.
        navigateToLoginRequestUrl: false,
      },
      cache: {
        cacheLocation: 'sessionStorage',
        // Store PKCE state in a cookie alongside sessionStorage. Without this,
        // the code_verifier for the PKCE exchange lives only in sessionStorage
        // and can be lost during redirects (privacy settings, SWA routing),
        // causing handleRedirectPromise() to silently fail → auth loop.
        storeAuthStateInCookie: true,
      },
      system: {
        allowNativeBroker: false,
      },
    } satisfies Configuration)
  : null;

/**
 * Acquire a silent token scoped to Microsoft Graph (User.Read).
 * Used by AuthGuard to forward a delegated token to the backend so it can
 * call Graph /me on behalf of the signed-in user.
 * Returns null if MSAL is not configured, the user is not signed in, or
 * silent acquisition fails (non-fatal — callers should fall back gracefully).
 */
export async function acquireGraphToken(): Promise<string | null> {
  if (!msalInstance) return null;
  try {
    await ensureReady();
  } catch {
    return null;
  }
  const accounts = msalInstance.getAllAccounts();
  if (!accounts.length) return null;

  try {
    const result = await msalInstance.acquireTokenSilent({
      scopes: ['User.Read'],
      account: accounts[0],
    });
    return result.accessToken;
  } catch {
    return null;
  }
}

// The SWA identity is read once per page load and used ONLY as an MSAL
// loginHint. It is never treated as an identity assertion - see the header of
// bearerBridge.ts for why that is a bounded, verifiable use.
let _swaHint: Promise<SwaIdentity> | null = null;

/** Hard ceiling on the identity probe. */
const SWA_HINT_TIMEOUT_MS = 3000;

async function readSwaIdentity(): Promise<SwaIdentity> {
  if (typeof window === 'undefined') return { state: 'no-swa' };
  if (!_swaHint) {
    // BOUNDED, and only memoised once it settles usefully.
    //
    // Every /api call awaits this and the result is cached, so an unbounded
    // fetch here would not slow one request - it would wedge the whole app
    // behind a single stalled probe, and caching the hung promise would keep
    // it wedged for the life of the page.
    //
    // The four outcomes are kept DISTINCT. Collapsing "probe failed" into
    // "not behind SWA" is what let a 3s timeout or a 502 fall through to the
    // first cached sessionStorage account without checking whose it was.
    const probe = (async (): Promise<SwaIdentity> => {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), SWA_HINT_TIMEOUT_MS);
      try {
        const res = await fetch('/.auth/me', { signal: controller.signal });
        // 404 means the SWA auth platform is not in front of us at all.
        if (res.status === 404) return { state: 'no-swa' };
        if (!res.ok) return { state: 'unknown' };

        let data: { clientPrincipal?: { userDetails?: string } | null };
        try {
          data = await res.json();
        } catch {
          // A non-JSON 200 is the dev server serving index.html - same
          // conclusion AuthGuard draws: SWA is not in front of us.
          return { state: 'no-swa' };
        }

        const upn = data?.clientPrincipal?.userDetails;
        if (typeof upn === 'string' && upn.trim()) return { state: 'signed-in', upn };
        return { state: 'signed-out' };
      } catch {
        // Aborted by the timeout, or a network error. We know nothing.
        return { state: 'unknown' };
      } finally {
        clearTimeout(timer);
      }
    })();

    _swaHint = probe;
    // Never freeze an inconclusive answer for the life of the page - let the
    // next call re-probe.
    probe
      .then((identity) => {
        if (identity.state === 'unknown') _swaHint = null;
      })
      .catch(() => {
        _swaHint = null;
      });
  }
  return _swaHint;
}

/** The current SWA UPN, for use as an MSAL loginHint only. */
async function readSwaLoginHint(): Promise<string | null> {
  const identity = await readSwaIdentity();
  return identity.state === 'signed-in' ? identity.upn : null;
}

/** Test/reset seam - drops the cached SWA identity probe. */
export function __resetSwaHintCache(): void {
  _swaHint = null;
}

/**
 * MSAL v3 lifecycle for this app: initialize(), then redeem any redirect
 * return, exactly once. Safe no-op outside a browser or when MSAL is not
 * configured. Every production MSAL call path goes through this first.
 */
export async function ensureReady(): Promise<void> {
  if (typeof window === 'undefined' || !msalInstance) return;
  const instance = msalInstance;
  await ensureMsalReady({
    initialize: () => instance.initialize(),
    handleRedirectPromise: () => instance.handleRedirectPromise(),
    setActiveAccount: (account) => instance.setActiveAccount(account as AccountInfo),
  });
  // Redirect redemption is not API acceptance. Keep the retry guard until
  // a server response validates the credential.
}

/** Production wiring for the bearer bridge. */
function bridgeDeps(): BearerBridgeDeps {
  const instance = msalInstance!;
  return {
    ready: ensureReady,
    // Dev builds only. In a production bundle import.meta.env.DEV is false and
    // Vite constant-folds it, so the unhinted-account branch is unreachable in
    // the deployed app regardless of what the identity probe returns.
    allowUnhintedAccount: import.meta.env.DEV === true,
    getAccounts: () => instance.getAllAccounts(),
    ssoSilent: async (req) => {
      const result = await instance.ssoSilent({
        scopes: req.scopes,
        loginHint: req.loginHint,
      });
      return result.account ?? null;
    },
    acquireTokenSilent: (req) =>
      instance.acquireTokenSilent({
        scopes: req.scopes,
        account: req.account as AccountInfo,
      }),
    getSwaIdentity: readSwaIdentity,
  };
}

/**
 * Acquire a bearer token for staff-gated /api calls, with the reason when one
 * could not be produced.
 *
 * Unlike the previous implementation this no longer gives up when MSAL holds
 * no account. MSAL caches accounts in sessionStorage while the SWA session
 * outlives it, so "SWA-authenticated but MSAL-empty" is the ordinary state
 * after a new tab or a browser restart - and since PR #630 that state means a
 * 401 on every staff-gated endpoint. The bridge recovers it via ssoSilent.
 */
export async function getBearer(): Promise<BearerResult> {
  if (!msalInstance || !clientId) return { token: null, reason: 'not-configured' };
  return acquireBearer(bridgeDeps(), apiScopeFor(clientId));
}

/**
 * Back-compatible accessor - same `string | null` contract every existing
 * caller already handles. Prefer getBearer() where the reason matters.
 */
export async function getAccessToken(): Promise<string | null> {
  return (await getBearer()).token;
}

/**
 * Escalate to an interactive MSAL sign-in. Full-page redirect, so it is
 * guarded to fire at most once per browsing session.
 *
 * This deliberately does NOT go to `/.auth/login/aad`. That endpoint
 * re-establishes the SWA session, which after PR #630 the API no longer
 * accepts - so bouncing through it returns the user to the same 401 and is
 * the redirect loop this replaces.
 */
export async function startInteractiveAuth(reason: BearerReason): Promise<boolean> {
  if (typeof window === 'undefined' || !msalInstance) return false;
  if (!shouldStartInteractiveAuth(reason, window.sessionStorage)) return false;
  try {
    // v3: loginRedirect throws uninitialized_public_client_application unless
    // initialize() has resolved first.
    await ensureReady();
  } catch {
    clearInteractiveAuthFlag(window.sessionStorage);
    return false;
  }
  const hint = await readSwaLoginHint();
  try {
    await msalInstance.loginRedirect({
      ...loginRequest,
      ...(hint ? { loginHint: hint } : {}),
    });
    return true;
  } catch {
    clearInteractiveAuthFlag(window.sessionStorage);
    return false;
  }
}

/** Re-arm interactive auth only after the server accepts the credential. */
export function noteBearerAcquired(): void {
  if (typeof window === 'undefined') return;
  clearInteractiveAuthFlag(window.sessionStorage);
}
